import {useEffect} from 'react';

import {GenericFunction} from 'util/Types';

export interface SupportsEvents {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(events: string, handler: GenericFunction): any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  off(events: string, handler: GenericFunction): any;
}

/**
 * React hook to respond to imperative events from JQuery or Backbone. Useful for listening to a Backbone model
 * change and responding to it from a React component. Handles the cleanup of removing the event listener too.
 *
 * Since `handler` is a dependency of the effect, users will likely want to wrap their callback in a useCallback hook
 * to avoid thrash of registering and unregistering events (and potentially missing some during the cross-over).
 *
 * Components that need to interact with events on their own DOM nodes like mouseover and hover events should
 * not use this hook and instead use props like onMouseOver.
 */
export default function useEventHandler(
  target: SupportsEvents | undefined,
  events: string,
  handler: GenericFunction
): void {
  useEffect(() => {
    target?.on(events, handler);
    return () => {
      target?.off(events, handler);
    };
  }, [target, events, handler]);
}
